import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  buySeats,
  cancelSubscription,
  changePlan,
  clearBillingContact,
  createBillingPortalSession,
  createCheckoutSession,
  getSelectablePlans,
  getSubscription,
  previewCancellation,
  releaseSeats,
  setBillingContact,
  withdrawCancellation,
} from '../api/billingApi'
import { PERMISSIONS_QUERY_KEY } from '@/entities/session/model/keys'
import type { Cancellation, SubscriptionResponse } from './types'
import { billingKeys } from './keys'

export function useSubscription(enabled = true) {
  return useQuery({
    queryKey: billingKeys.subscription(),
    queryFn: getSubscription,
    enabled,
    // Billing state changes rarely, but when it does the old answer is actively misleading ("behind
    // on payment" on an account that is Trialing again), so it is never trusted without asking:
    // always stale, hence refetched on mount and on window focus. That is not polling; nothing
    // refetches on a timer. The key is also kept out of the persisted cache (see
    // UNPERSISTED_QUERY_KEY_PREFIXES), so a reload starts empty rather than from yesterday's state.
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  })
}

/**
 * The plan catalogue the signup picker reads. Unlike the subscription, this is the published price
 * list: it changes only when we reprice, so it is cached for the session rather than re-asked on
 * every mount, and the server validates the chosen code again when the org is created.
 */
export function usePlanOptions(enabled = true) {
  return useQuery({
    queryKey: billingKeys.plans(),
    queryFn: getSelectablePlans,
    enabled,
    staleTime: Infinity,
  })
}

/**
 * Moves a trialling account between plans.
 *
 * The response is the whole subscription, so it is written straight into the cache the billing page
 * reads: the new caps are on screen before any refetch comes back. It is still invalidated after
 * that, because the usage counts in the response were read server-side a moment ago and a site may
 * have been switched on since.
 *
 * No toast here. A plan change is confirmed by the page it is made from — the plan name, the badge
 * and both usage meters change under the dialog as it closes — and a refusal is shown in the dialog,
 * which is where the person is looking.
 */
export function useChangePlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: changePlan,
    onSuccess: (subscription) => {
      queryClient.setQueryData(billingKeys.subscription(), subscription)
      void queryClient.invalidateQueries({ queryKey: billingKeys.subscription() })
    },
  })
}

/**
 * Buys users beyond the plan's allowance.
 *
 * The response is NOT written straight into the subscription cache the way `useChangePlan`'s is:
 * `SeatChangeResponse` is the two seat figures, not a whole `SubscriptionResponse`, so the page
 * refetches instead. That also picks up the cost figures, which the API recomputed.
 *
 * No error toast — the dialogue shows the message. See `buySeats`.
 */
export function useBuySeats() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: buySeats,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.subscription() })
    },
  })
}

/**
 * Gives bought users back, applied at the end of the period already paid for.
 *
 * Invalidates the same query for the same reason: `purchasedSeats` deliberately does not move yet,
 * but `seatsReleaseAtPeriodEnd` does, and the meter reads both.
 */
export function useReleaseSeats() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: releaseSeats,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.subscription() })
    },
  })
}

/**
 * What cancelling now would do. Read only when the cancel card is actually on screen, because it
 * needs `billing.manage` and asking without it would be a 403 on every billing page load.
 *
 * `staleTime: 0` for the same reason as the subscription: the dates it reports move with the
 * account's state, and a stale preview would put a wrong date in front of someone about to confirm.
 */
export function useCancellationPreview(enabled: boolean) {
  return useQuery({
    queryKey: billingKeys.cancellation(),
    queryFn: previewCancellation,
    enabled,
    staleTime: 0,
    refetchOnMount: true,
  })
}

/**
 * Cancels at the end of the term, or withdraws a cancellation.
 *
 * Both invalidate the subscription AND the permissions, even though neither changes what the account
 * can do today. Cancelling is the step that eventually withdraws write access, and a user who
 * cancels and then withdraws within the same session must not be left holding a permission set that
 * was projected from a state they have already left. The preview is invalidated too: its answer
 * ("you cannot cancel, one is already scheduled") changes with every one of these.
 */
function useCancellationMutation(mutationFn: () => Promise<Cancellation>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.subscription() })
      void queryClient.invalidateQueries({ queryKey: billingKeys.cancellation() })
      void queryClient.invalidateQueries({ queryKey: PERMISSIONS_QUERY_KEY })
    },
  })
}

export function useCancelSubscription() {
  return useCancellationMutation(cancelSubscription)
}

export function useWithdrawCancellation() {
  return useCancellationMutation(withdrawCancellation)
}

/**
 * Nominates or removes the billing contact.
 *
 * Both answer with the whole subscription, so it goes straight into the cache the page reads and the
 * card shows the stored value without waiting for a refetch. Invalidated afterwards anyway, for the
 * reason `useChangePlan` gives: the usage counts in the response were read a moment ago server-side.
 *
 * The permissions are deliberately NOT invalidated — unlike cancelling, nothing about a billing
 * address changes what anybody may do.
 *
 * No toast. The card is the confirmation: the stored address appears in it, and a refusal is shown
 * inline, which is where the person is looking.
 */
function useBillingContactMutation<TArg>(mutationFn: (arg: TArg) => Promise<SubscriptionResponse>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: (subscription) => {
      queryClient.setQueryData(billingKeys.subscription(), subscription)
      void queryClient.invalidateQueries({ queryKey: billingKeys.subscription() })
    },
  })
}

export function useSetBillingContact() {
  return useBillingContactMutation(setBillingContact)
}

/** `void` so the caller can write `mutateAsync()`: there is nothing to remove it to. */
export function useClearBillingContact() {
  return useBillingContactMutation<void>(() => clearBillingContact())
}

/**
 * Opens Stripe's hosted billing portal.
 *
 * A mutation rather than a query, because the URL is single-use and creating one is a side effect: a
 * `useQuery` would be free to refetch on focus and quietly burn sessions. Nothing is cached.
 *
 * It deliberately does NOT invalidate anything on success — at that point the customer has not done
 * anything in the portal yet. The refetch happens when they come BACK, which the billing page handles
 * from the `?billing=returned` marker Stripe returns them with.
 */
export function useBillingPortalSession() {
  return useMutation({ mutationFn: createBillingPortalSession })
}

/**
 * Opens a Checkout session.
 *
 * Nothing is invalidated on success, for the same reason the portal's hook invalidates nothing: at
 * that moment the customer has not paid, they have only been handed a URL. What makes them
 * subscribed are Stripe's webhooks, which land independently of the browser coming back — on an
 * account with no trial left, the paid invoice has been seen to arrive before the session-completed
 * event. So the page must REFETCH after the return rather than infer anything from `?billing=returned`.
 */
export function useCheckoutSession() {
  return useMutation({ mutationFn: createCheckoutSession })
}
